import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { undoableDelete } from "../lib/undo";
import { toast } from "sonner";
import { HandHeart, Trash2, Clock, Lock, UserCheck, Sparkles, Check, X } from "lucide-react";
import { usePaged, Paginator } from "../components/Paginator";
import { useConfirm } from "../context/ConfirmContext";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const STATUS = {
  new: { label: "New", bg: "#0B5E4E1A", color: "#0B5E4E" },
  praying: { label: "Praying", bg: "#0844381A", color: "#084438" },
  answered: { label: "Answered", bg: "#17A88A1A", color: "#17A88A" },
};

export default function PrayerRequests() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [pending, setPending] = useState([]);
  const confirm = useConfirm();

  const load = async () => {
    try { const { data } = await api.get("/prayer-requests", { params: filter !== "all" ? { status: filter } : {} }); setItems(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const loadPending = async () => {
    try { const { data } = await api.get("/testimonies/pending"); setPending(data || []); } catch {}
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line
  useEffect(() => { loadPending(); }, []);

  const approveTestimony = async (id) => {
    try { await api.post(`/testimonies/${id}/approve`); toast.success("Testimony approved — now on the wall 🙌"); loadPending(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const rejectTestimony = async (id) => {
    if (!(await confirm({ title: "Remove this testimony?", description: "It won't appear on the Testimony Wall. The member keeps their private testimony and can re-share it later.", confirmLabel: "Remove" }))) return;
    try { await api.post(`/testimonies/${id}/reject`); toast.success("Removed from the wall"); loadPending(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setStatus = async (id, status) => {
    try { await api.put(`/prayer-requests/${id}/status`, { status }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = (id) => {
    setItems((l) => l.filter((x) => x.id !== id));
    undoableDelete({ label: "Prayer request", entityType: "prayer-request", perform: () => api.delete(`/prayer-requests/${id}`), onUndo: load });
  };
  const { page, setPage, pageCount, pageItems } = usePaged(items, 25);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="sj-page-hero">
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Prayer Requests</h1>
        <p style={{ color: "var(--sj-text-2)" }}>Requests submitted by members — pray over them and mark your progress.</p>
      </div>

      {pending.length > 0 && (
        <div className={`${CARD} p-6`} data-testid="testimony-review-queue" style={{ borderColor: "#17A88A55" }}>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
            <h2 className="font-head text-xl font-semibold">Testimonies awaiting review</h2>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "#FBEAD7", color: "#8a5a1a" }} data-testid="testimony-pending-count">{pending.length}</span>
          </div>
          <p className="text-sm mb-4" style={{ color: "var(--sj-text-2)" }}>Approve to publish on the members' Testimony Wall, or remove to keep it off the wall.</p>
          <div className="space-y-3">
            {pending.map((t) => (
              <div key={t.id} data-testid={`testimony-pending-${t.id}`} className="rounded-xl border p-4" style={{ borderColor: "var(--sj-border)" }}>
                <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--sj-text)" }}>{t.testimony}</p>
                <div className="text-xs mt-1.5" style={{ color: "var(--sj-text-3)" }}>— {t.name}{t.date ? ` · ${new Date(t.date).toLocaleDateString('en-GB', { month: "short", day: "numeric" })}` : ""}</div>
                <div className="mt-3 flex gap-2">
                  <button data-testid={`testimony-approve-${t.id}`} onClick={() => approveTestimony(t.id)} className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-4 py-2 text-white hover:brightness-105" style={{ background: "var(--sj-primary)" }}><Check className="h-3.5 w-3.5" /> Approve &amp; publish</button>
                  <button data-testid={`testimony-reject-${t.id}`} onClick={() => rejectTestimony(t.id)} className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-4 py-2 border hover:bg-[#FDE7E7]" style={{ borderColor: "var(--sj-border)", color: "#B91C1C" }}><X className="h-3.5 w-3.5" /> Remove</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {[["all", "All"], ["new", "New"], ["praying", "Praying"]].map(([s, label]) => (
          <button key={s} data-testid={`prayer-filter-${s}`} onClick={() => setFilter(s)}
            className="rounded-full px-4 py-2 text-sm font-medium border transition-all"
            style={filter === s ? { background: "var(--sj-secondary)", color: "#fff", borderColor: "var(--sj-secondary)" } : { background: "#fff", color: "var(--sj-text-2)", borderColor: "var(--sj-border)" }}>
            {label}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className={`${CARD} p-16 text-center flex flex-col items-center gap-3`} style={{ color: "var(--sj-text-3)" }}>
          <HandHeart className="h-12 w-12" />
          <div>No prayer requests here yet.</div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="prayer-list">
          {pageItems.map((p) => {
            const st = STATUS[p.status] || STATUS.new;
            return (
              <div key={p.id} data-testid={`prayer-${p.id}`} className={`${CARD} p-5`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 font-semibold" style={{ color: "var(--sj-text)" }}>
                      {p.member_name}
                      {p.is_private && <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#56626A1A", color: "#56626A" }}><Lock className="h-3 w-3" /> Confidential</span>}
                    </div>
                    <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>{new Date(p.created_at).toLocaleString('en-GB', { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                  <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full shrink-0" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                </div>
                <p className="mt-3 text-sm whitespace-pre-wrap" style={{ color: "var(--sj-text)" }}>{p.request}</p>
                {(p.answered_by || p.last_action_by) && (
                  <div className="mt-2 text-xs flex items-center gap-1.5" style={{ color: "var(--sj-text-3)" }} data-testid={`prayer-actor-${p.id}`}>
                    <UserCheck className="h-3.5 w-3.5" />
                    {p.status === "answered" && p.answered_by
                      ? `Answered by ${p.answered_by}`
                      : `Last updated by ${p.last_action_by}`}
                    {(p.answered_at || p.last_action_at) && ` · ${new Date(p.answered_at || p.last_action_at).toLocaleDateString('en-GB', { month: "short", day: "numeric" })}`}
                  </div>
                )}
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <button data-testid={`prayer-praying-${p.id}`} onClick={() => setStatus(p.id, "praying")} disabled={p.status === "praying"}
                    className="flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border hover:bg-[#EDF2F4] disabled:opacity-50" style={{ borderColor: "var(--sj-border)", color: "#084438" }}><Clock className="h-3.5 w-3.5" /> Mark Praying</button>
                  <button data-testid={`prayer-delete-${p.id}`} onClick={() => del(p.id)} className="flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border hover:bg-[#E3F1EC] ml-auto" style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Paginator page={page} pageCount={pageCount} onPage={setPage} testid="prayers-paginator" />
    </div>
  );
}
